<script setup lang="ts">
import axios from 'axios'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { type AdverseReportResult, type ReportGroup, type ReportRecord, type ReportSelection, type ReportTab, reportFilters, reportPeriodError } from '../reports'
import { adverseSearchAdapter, adverseSearchDefaults } from '../utils/searchAdapter'
import { adverseSearchState } from '../utils/searchFilters'
import AdverseReportMetrics from './AdverseReportMetrics.vue'
import UniversalSearchFilter from '@/components/searchFilter/UniversalSearchFilter.vue'
import type { FilterState } from '@/components/searchFilter/types'
import DateMaskInput from '@/components/createdUi/DateMaskInput.vue'
import { getApiError } from '@/composables/getApiError'
import { BASE_URL } from '@/environment/environment'
import { createPreviewScheduler } from '@/modules/reportTemplates/preview'
import { ERouteNames } from '@/router/ERouteNames'

const route = useRoute()
const router = useRouter()
const queryString = (key: string) => typeof route.query[key] === 'string' ? route.query[key] as string : ''
const tab = ref<ReportTab>((['summary', 'consequences', 'questionnaires', 'effectiveness'].includes(queryString('tab')) ? queryString('tab') : 'summary') as ReportTab)
const group = ref<ReportGroup>((['department', 'coordinator', 'type'].includes(queryString('group')) ? queryString('group') : 'department') as ReportGroup)
const questionnaire = ref(queryString('questionnaire'))
const filters = ref<FilterState>(adverseSearchState({ search: queryString('search'), advanced: queryString('advanced') }))
const after = ref<string | null>(queryString('created_after') || null)
const before = ref<string | null>(queryString('created_before') || null)
const dateErrors = ref({ after: '', before: '' })
const periodError = computed(() => dateErrors.value.after || dateErrors.value.before || reportPeriodError(after.value, before.value))
const periodKey = ref(0)
watch(after, () => {
  dateErrors.value.after = ''
})
watch(before, () => {
  dateErrors.value.before = ''
})
const selection = ref<ReportSelection | null>(null)
const page = ref(1)
const pageSize = ref(25)
const data = ref<AdverseReportResult | null>(null)
const loading = ref(false)
const error = ref('')
const exportError = ref('')
const exporting = ref(false)
const selectedRecord = ref<ReportRecord | null>(null)
const detailsElement = ref<HTMLElement | null>(null)
const pdfUrl = ref('')
const pdfDialog = ref(false)
let exportController: AbortController | null = null
const questionnaireOptions = ref<Array<{ value: string; label: string }>>([])
const apiUrl = `${BASE_URL}/api/adverse/adverse-event/reports/`
const payload = computed(() => ({
  tab: tab.value,
  group: group.value,
  questionnaire: questionnaire.value,
  filters: reportFilters(filters.value, after.value, before.value),
  selection: selection.value,
  page: page.value,
  page_size: pageSize.value,
}))
type ReportPayload = typeof payload.value
let loadedPayload: ReportPayload | null = null
const groups = [{ value: 'department', title: 'Отделения' }, { value: 'coordinator', title: 'Координаторы' }, { value: 'type', title: 'Вид НС' }]
const detailsHeaders = computed(() => [
  ...(data.value?.columns || []).map(column => ({ ...column, sortable: false })),
  ...(tab.value !== 'summary' ? [{ key: 'answers', title: 'Ответы', sortable: false }] : []),
])
const canExport = computed(() => !!data.value && !loading.value && !exporting.value && !periodError.value && !error.value)
const scheduler = createPreviewScheduler<ReportPayload, { report: AdverseReportResult; input: ReportPayload }>({
  delay: 150,
  load: async (input, signal) => ({ report: (await axios.post<AdverseReportResult>(apiUrl, input, { signal })).data, input }),
  pending: () => {
    loading.value = true
    error.value = ''
    exportError.value = ''
  },
  resolved: ({ report, input }) => {
    data.value = report
    questionnaireOptions.value = report.questionnaires
    loadedPayload = input
    loading.value = false
  },
  rejected: cause => {
    data.value = null
    error.value = getApiError(cause, 'Не удалось загрузить отчёт')
    loading.value = false
  },
})

function load() {
  exportController?.abort()
  if (periodError.value) {
    scheduler.cancel()
    data.value = null
    loading.value = false
    return
  }
  scheduler.schedule(payload.value)
  router.replace({
    query: {
      tab: tab.value, group: group.value, questionnaire: questionnaire.value || undefined, ...payload.value.filters,
    },
  })
}

watch([tab, group, questionnaire, filters, after, before], () => {
  selection.value = null
  page.value = 1
  data.value = null
}, { deep: true, flush: 'sync' })
watch(pageSize, () => {
  page.value = 1
})
watch([payload, periodError], load, { immediate: true })

async function selectMetric(value: ReportSelection) {
  selection.value = value
  page.value = 1
  await nextTick()
  detailsElement.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function resetSelection() {
  selection.value = null
  page.value = 1
}

function resetPeriod() {
  after.value = null
  before.value = null
  dateErrors.value = { after: '', before: '' }
  periodKey.value++
}

function checkDate(side: 'after' | 'before', event: FocusEvent) {
  const input = event.target
  if (input instanceof HTMLInputElement && input.value && !/^\d{2}\.\d{2}\.\d{4}$/.test(input.value)) {
    dateErrors.value[side] = 'Укажите дату полностью.'
  }
}

async function exportFile(output: 'xlsx' | 'pdf', detailsOnly = false) {
  if (!canExport.value || !loadedPayload) return
  exporting.value = true
  exportError.value = ''
  const controller = new AbortController()
  exportController = controller
  const filename = `НС — ${data.value?.title || 'Отчёт'}.xlsx`
  try {
    const result = await axios.post<Blob>(apiUrl, {
      ...loadedPayload, selection: detailsOnly ? loadedPayload.selection : null, details_only: detailsOnly, output,
    }, { responseType: 'blob', signal: controller.signal })
    if (controller.signal.aborted) return
    const url = URL.createObjectURL(result.data)
    if (output === 'pdf') {
      if (pdfUrl.value) URL.revokeObjectURL(pdfUrl.value)
      pdfUrl.value = url
      pdfDialog.value = true
    } else {
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    }
  } catch (cause: any) {
    if (controller.signal.aborted) return
    if (cause.response?.data instanceof Blob) {
      try {
        cause.response.data = JSON.parse(await cause.response.data.text())
      } catch { /* Use the fallback message. */ }
    }
    exportError.value = getApiError(cause, 'Не удалось сформировать файл')
  } finally {
    exporting.value = false
  }
}

onBeforeUnmount(() => {
  scheduler.cancel()
  exportController?.abort()
  if (pdfUrl.value) URL.revokeObjectURL(pdfUrl.value)
})
</script>

<template>
  <div class="adverse-reports d-flex flex-column gap-4">
    <div class="d-flex align-center justify-space-between flex-wrap gap-3">
      <div class="d-flex align-center gap-3">
        <VBtn
          :to="{ name: ERouteNames.ADVERSE_EVENTS }"
          variant="text"
          icon="ri-arrow-left-line"
          aria-label="К списку НС"
        />
        <h2 class="text-h5">
          Отчёты по НС
        </h2>
      </div>
      <div class="d-flex gap-2">
        <VBtn
          variant="outlined"
          prepend-icon="ri-file-excel-2-line"
          :disabled="!canExport"
          :loading="exporting"
          @click="exportFile('xlsx')"
        >
          Excel
        </VBtn>
        <VBtn
          variant="outlined"
          prepend-icon="ri-printer-line"
          :disabled="!canExport"
          @click="exportFile('pdf')"
        >
          Печать
        </VBtn>
      </div>
    </div>
    <VCard class="overflow-visible">
      <VCardText class="d-flex flex-column gap-4">
        <UniversalSearchFilter
          :model-value="filters"
          :adapter="adverseSearchAdapter"
          :defaults="adverseSearchDefaults"
          scope-label="Анкеты"
          add-placeholder="Добавить поле или анкету"
          @update:model-value="filters = $event"
        />
        <div :key="periodKey" class="adverse-reports__period">
          <DateMaskInput
            v-model="after"
            hide-details="auto"
            @invalid="dateErrors.after = $event"
            @focusout="checkDate('after', $event)"
          >
            <template #label>
              Дата НС с
            </template>
          </DateMaskInput>
          <DateMaskInput
            v-model="before"
            hide-details="auto"
            @invalid="dateErrors.before = $event"
            @focusout="checkDate('before', $event)"
          >
            <template #label>
              Дата НС по
            </template>
          </DateMaskInput>
          <VBtn variant="text" @click="resetPeriod">
            Весь период
          </VBtn>
        </div>
        <VAlert v-if="periodError" type="error" variant="tonal">
          {{ periodError }}
        </VAlert>
      </VCardText>
      <VTabs v-model="tab" show-arrows>
        <VTab value="summary">
          Сводка
        </VTab>
        <VTab value="consequences">
          Последствия
        </VTab>
        <VTab value="questionnaires">
          Анкеты
        </VTab>
        <VTab value="effectiveness">
          Результативность
        </VTab>
      </VTabs>
    </VCard>
    <div class="adverse-reports__selector">
      <VSelect
        v-if="tab === 'summary'"
        v-model="group"
        :items="groups"
        label="Группировать по"
        hide-details
      />
      <VSelect
        v-if="tab === 'questionnaires' && questionnaireOptions.length"
        :model-value="questionnaire || data?.questionnaire"
        :items="questionnaireOptions"
        item-title="label"
        item-value="value"
        label="Анкета"
        hide-details
        @update:model-value="questionnaire = $event"
      />
    </div>
    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      role="alert"
    >
      {{ error }} <VBtn variant="text" @click="load">
        Повторить
      </VBtn>
    </VAlert>
    <VAlert
      v-if="exportError"
      type="error"
      variant="tonal"
      role="alert"
    >
      {{ exportError }}
    </VAlert>
    <VProgressLinear v-if="loading" indeterminate color="primary" />
    <template v-if="data">
      <div class="d-flex flex-wrap gap-3 align-center text-body-2" aria-live="polite">
        <span>НС в выборке: <strong>{{ data.event_count }}</strong></span>
        <template v-if="tab !== 'summary'">
          <span v-if="data.applicable_event_count !== data.event_count">Анкета подключена: <strong>{{ data.applicable_event_count }} НС</strong></span>
          <span v-if="data.unit !== 'НС'">{{ data.unit }}: <strong>{{ data.record_count }}</strong></span>
          <span v-if="data.unit !== 'НС'">Связанных НС: <strong>{{ data.related_event_count }}</strong></span>
          <VTooltip location="top" max-width="360">
            <template #activator="{ props }">
              <VBtn
                v-bind="props"
                variant="text"
                size="small"
                icon="ri-question-line"
                aria-label="Как считаются показатели"
              />
            </template>
            В одном НС может быть несколько анкет или мероприятий. Вопросы учитываются по текущим ответам. Пустой ответ не означает «Нет».
          </VTooltip>
        </template>
      </div>
      <VAlert v-if="!data.record_count" type="info" variant="tonal">
        {{ data.event_count ? 'Для выбранных НС нет записей этого отчёта.' : 'По выбранным условиям НС не найдены.' }}
      </VAlert>
      <div v-if="data.record_count" class="adverse-reports__sections" :class="{ 'adverse-reports__sections--summary': tab === 'summary' }">
        <AdverseReportMetrics
          v-for="section in data.sections"
          :key="section.key"
          :section="section"
          :disabled="loading"
          @select="selectMetric"
        />
      </div>
      <section ref="detailsElement" class="adverse-reports__details">
        <VCard>
          <VCardText class="d-flex flex-wrap align-center justify-space-between gap-3">
            <div>
              <h3 class="text-h6">
                {{ selection ? 'Записи по показателю' : 'Записи отчёта' }}
              </h3>
              <div v-if="selection" class="text-body-2 mt-1">
                {{ data.details.label }}
              </div>
              <div class="text-body-2 mt-1">
                Записей: {{ data.details.count }} · НС: {{ data.details.events }}
              </div>
            </div>
            <div class="d-flex flex-wrap gap-2">
              <VBtn v-if="selection" variant="text" @click="resetSelection">
                Все записи
              </VBtn>
              <VBtn variant="text" :disabled="!canExport" @click="exportFile('xlsx', true)">
                Excel
              </VBtn>
              <VBtn variant="text" :disabled="!canExport" @click="exportFile('pdf', true)">
                Печать
              </VBtn>
            </div>
          </VCardText>
          <VDataTableServer
            v-model:page="page"
            v-model:items-per-page="pageSize"
            :headers="detailsHeaders"
            :items="data.details.rows"
            :items-length="data.details.count"
            :loading="loading"
            :items-per-page-options="[25, 50, 100]"
            no-data-text="Нет записей"
            loading-text="Загрузка…"
            items-per-page-text="Строк на странице"
            class="adverse-reports__records"
          >
            <template #item.number="{ item }">
              <RouterLink :to="router.resolve({ name: ERouteNames.ADVERSE_EVENT_DETAIL, params: { id: item.event_id } }).href" target="_blank" rel="noopener noreferrer">
                {{ item.number }}
              </RouterLink>
            </template>
            <template #item.result="{ value }">
              {{ value || '—' }}
            </template>
            <template #item.answers="{ item }">
              <VBtn
                variant="text"
                size="small"
                :disabled="loading"
                @click="selectedRecord = item"
              >
                Ответы
              </VBtn>
            </template>
          </VDataTableServer>
        </VCard>
      </section>
    </template>
    <VDialog :model-value="!!selectedRecord" max-width="900" @update:model-value="selectedRecord = null">
      <VCard v-if="selectedRecord">
        <VCardTitle class="d-flex align-center justify-space-between gap-2">
          <span>НС № {{ selectedRecord.number }}</span>
          <VBtn
            variant="text"
            icon="ri-close-line"
            aria-label="Закрыть"
            @click="selectedRecord = null"
          />
        </VCardTitle>
        <VCardText>
          <div class="mb-3">
            {{ selectedRecord.subject }}
          </div>
          <VTable class="adverse-reports__answers">
            <thead><tr><th>Вопрос</th><th>Ответ</th></tr></thead>
            <tbody>
              <tr v-for="answer in selectedRecord.answers" :key="answer.code">
                <td>{{ answer.question }}</td><td>{{ answer.value }}</td>
              </tr>
            </tbody>
          </VTable>
        </VCardText>
      </VCard>
    </VDialog>
    <VDialog v-model="pdfDialog" max-width="1200">
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          Печать отчёта <VBtn
            variant="text"
            icon="ri-close-line"
            aria-label="Закрыть"
            @click="pdfDialog = false"
          />
        </VCardTitle>
        <VCardText>
          <VBtn
            :href="pdfUrl"
            target="_blank"
            rel="noopener noreferrer"
            variant="text"
            class="mb-2"
          >
            Открыть PDF
          </VBtn>
          <iframe :src="pdfUrl" title="Печатная форма отчёта" class="adverse-reports__pdf" />
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped lang="scss">
.adverse-reports {
  min-inline-size: 0;
  &__period {
    display: grid;
    grid-template-columns: minmax(160px, 220px) minmax(160px, 220px) max-content;
    gap: 16px;
    align-items: center;
    @media (max-width: 600px) { grid-template-columns: minmax(0, 1fr); }
  }
  &__selector { max-inline-size: 540px; }
  &__sections {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    align-items: start;
    &--summary { grid-template-columns: minmax(0, 1fr); }
    @media (max-width: 960px) { grid-template-columns: minmax(0, 1fr); }
  }
  &__details { min-inline-size: 0; scroll-margin-block-start: 80px; }
  &__records :deep(th) { background: rgba(var(--v-theme-primary), .12); white-space: nowrap; }
  &__records :deep(td) { min-inline-size: 90px; max-inline-size: 320px; overflow-wrap: anywhere; }
  &__answers :deep(td) { white-space: pre-wrap; overflow-wrap: anywhere; padding-block: 12px !important; }
  &__pdf { border: 0; inline-size: 100%; block-size: 72vh; }
}
</style>
